/**
 * Environment configuration with type safety
 * This module ensures all environment variables are properly validated
 */

// Define the structure of our environment variables
interface Env {
  supabase: {
    url: string;
    anonKey: string;
  };
  app: {
    url: string;
  };
  api: {
    assemblyAiKey: string;
  };
}

/**
 * Helper function to validate environment variables
 * Ensures required variables are present and provides warnings for missing values
 */
function validateEnv<T>(name: string, value: T | undefined, required: boolean = true, fallback?: T): T {
  // If value is missing or empty
  if (value === undefined || value === '') {
    // Log clear warning when environment variables are missing
    
    
    // If this is a required variable, throw an error
    if (required) {

      throw new Error(`Required environment variable ${name} is missing`);
    }
    
    // Return fallback or empty string if not required
    return (fallback !== undefined) ? fallback : ('' as unknown as T);
  }
  
  // Value exists, return it
  return value;
}

/**
 * SUPABASE CONFIGURATION
 * 
 * We use environment variables exclusively for Supabase configuration.
 * No hardcoded fallbacks are used to ensure security and clarity.
 */

// Add function to check if environment is properly configured
function checkEnvironmentSetup() {
  const hasEnvFile = process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_ANON_KEY;
  
  if (!hasEnvFile && process.env.NODE_ENV === 'development') {
    
  }
  
  return hasEnvFile;
}

// Run check at module load time
checkEnvironmentSetup();

// Export this so it can be used elsewhere in the application
// Use only the specific environment variables from .env.local
export const hasValidEnvironment = process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_ANON_KEY;

// Parse and validate environment variables
export const env: Env = {
  supabase: {
    // Prioritize actual environment variables with better validation
    url: validateEnv(
      'Supabase URL', 
      process.env.REACT_APP_SUPABASE_URL, 
      true // Required - no fallbacks
    ),
    anonKey: validateEnv(
      'Supabase Anon Key', 
      process.env.REACT_APP_SUPABASE_ANON_KEY, 
      true // Required - no fallbacks
    ),
  },
  app: {
    url: validateEnv('REACT_APP_URL', process.env.REACT_APP_URL, false) || 
      (typeof window !== 'undefined' && process.env.NODE_ENV === 'production' 
        ? window.location.origin 
        : 'http://localhost:3000'),
  },
  api: {
    assemblyAiKey: validateEnv(
      'AssemblyAI API Key', 
      // Use only the exact variable name from .env.local
      process.env.REACT_APP_ASSEMBLY_AI_API_KEY, 
      false // Optional
    ),
  },
};

// Additional validation can be added here
// For example, URL format validation, key format validation, etc.

// Export the validated environment variables
export default env;
